// ✅ Trekkin.app - useEventRouteRenderer.js v1.5.1 (LIVE EVENT-GATE + GRACE OFF + ALT COLORS)
// -----------------------------------------------------------
// - Base = v1.5.0 (LIVE STRICT + ALT COLORS) → nada removido, só corrigido
// - FIX REAL:
//     • LIVE passa a ser determinado pelo EVENTO (ev.isLive === true)
//     • group.activeEventId serve para apontar o evento ativo, mas NÃO decide LIVE sozinho
//     • Grace de 1200ms: se activeEventId ficar null em transição, NÃO limpa logo (evita flicker)
// - Aceita events e groups tanto como array como objeto
// - Trilhos alternativos: cada um com cor própria (paleta fixa)
// -----------------------------------------------------------

import { useEffect, useRef } from "react";
import L from "leaflet";
import {
  normalizeTrekkinUrl,
  buildGpxFetchUrl,
} from "../utils/normalizeTrekkinUrl.js";
import { toLeafletOptimizedPolyline } from "../utils/polylineOptimization.js";
import { parseGPX } from "../functions/parseGPX.js";

// 🎨 Paleta fixa para trilhos alternativos
const ALT_COLORS = [
  "#ff0055",
  "#007bff",
  "#ff8800",
  "#00c9a7",
  "#aa00ff",
  "#00aa55",
  "#ff4444",
  "#ffaa00",
];
const TOUR_COLORS = [
  "#0f5132",
  "#0d6efd",
  "#fd7e14",
  "#198754",
  "#6f42c1",
  "#20c997",
];

const isEventRouteDebugEnabled = () => {
  try {
    return typeof window !== "undefined" && window.localStorage?.getItem("debugEventRoute") === "1";
  } catch {
    return false;
  }
};

const routeLog = (...args) => {
  if (!isEventRouteDebugEnabled()) return;
  console.log(...args);
};

export function useEventRouteRenderer({
  mapReady,
  isViewerMode,
  mapRef,
  currentGroup,
  events,
  groups,
  eventCacheEntry,
  canCacheLiveEvent,
  persistEventRouteCache,
  getEventRouteForMap,
  listenEvents, // compat; pode vir da useEventsStore
  eventRouteLayerRef,
  eventDirectionLayerRef,
  altEventRoutesRef,
  extraEventRoutesRef,
  groupMarkersRef,
  lastEventRouteKeyRef,
  routePointsRef,
  onRoutePointsChange,
}) {
  const eventsReadyRef = useRef(false);

  // 🧯 Grace timer para não limpar em "transição"
  const pendingClearTimerRef = useRef(null);
  const pendingClearReasonRef = useRef(null);

  const clearRoute = (reason = "unknown", options = {}) => {
    try {
      const map = mapRef.current;
      if (!map) return;

      if (eventRouteLayerRef.current) {
        map.removeLayer(eventRouteLayerRef.current);
        eventRouteLayerRef.current = null;
      }

      if (eventDirectionLayerRef?.current) {
        eventDirectionLayerRef.current.clearLayers();
      }

      const alts = altEventRoutesRef.current || {};
      Object.keys(alts).forEach((id) => {
        map.removeLayer(alts[id]);
        delete alts[id];
      });

      if (extraEventRoutesRef?.current) {
        const extraRoutes = extraEventRoutesRef.current || {};
        Object.keys(extraRoutes).forEach((eventId) => {
          if (extraRoutes[eventId]?.layer) {
            map.removeLayer(extraRoutes[eventId].layer);
          }
          delete extraRoutes[eventId];
        });
      }

      if (groupMarkersRef?.current) {
        const memberMarkers = groupMarkersRef.current || {};
        Object.keys(memberMarkers).forEach((markerId) => {
          map.removeLayer(memberMarkers[markerId]);
          delete memberMarkers[markerId];
        });
      }

      if (!options?.keepKey) {
        lastEventRouteKeyRef.current = null;
      }

      if (routePointsRef?.current) {
        routePointsRef.current = { main: [], alts: {} };
        if (typeof onRoutePointsChange === "function") {
          onRoutePointsChange();
        }
      }

      routeLog("[EVENT ROUTE] 🔄 clearRoute()", reason);
    } catch (e) {
      console.warn("[EVENT ROUTE] clearRoute falhou:", e);
    }
  };

  const cancelPendingClear = () => {
    try {
      if (pendingClearTimerRef.current) {
        clearTimeout(pendingClearTimerRef.current);
        pendingClearTimerRef.current = null;
        pendingClearReasonRef.current = null;
      }
    } catch {}
  };

  const scheduleClear = (reason) => {
    // se já há timer igual, não recria
    if (pendingClearTimerRef.current && pendingClearReasonRef.current === reason)
      return;

    cancelPendingClear();

    pendingClearReasonRef.current = reason;

    // Grace curta: evita limpar durante "transições" do Firebase/UI
    pendingClearTimerRef.current = setTimeout(() => {
      pendingClearTimerRef.current = null;
      const r = pendingClearReasonRef.current || reason;
      pendingClearReasonRef.current = null;
      clearRoute(r);
    }, 1200);
  };

  useEffect(() => {
    // Normalizar groups e events para arrays
    const groupsArr = Array.isArray(groups)
      ? groups
      : groups && typeof groups === "object"
      ? Object.values(groups)
      : [];

    const eventsArr = Array.isArray(events)
      ? events
      : events && typeof events === "object"
      ? Object.values(events)
      : [];

    // 🧠 marcar quando os eventos realmente chegaram
    if (eventsArr.length > 0 && !eventsReadyRef.current) {
      eventsReadyRef.current = true;
      routeLog("[EVENT ROUTE] ✅ events agora disponíveis");
    }

    // ⛔ Aguarda autenticação completa antes de processar rotas
    if (!window.__TREKKIN_AUTH_READY__) {
      routeLog("[EVENT ROUTE] ⏳ auth ainda não pronto");
      return;
    }

    routeLog("[EVENT ROUTE] 🔍 useEffect run", {
      mapReady,
      isViewerMode,
      currentGroup,
      groupsCount: groupsArr.length,
      eventsCount: eventsArr.length,
    });

    if (!mapReady) {
      routeLog("[EVENT ROUTE] pausa: mapReady = false");
      return;
    }
    if (isViewerMode) {
      routeLog("[EVENT ROUTE] pausa: viewer mode (sem rota de evento)");
      return;
    }

    const map = mapRef.current;
    if (!map) {
      routeLog("[EVENT ROUTE] erro: mapRef.current inexistente");
      return;
    }

    const clonePoints = (pts) =>
      Array.isArray(pts) ? pts.map((point) => ({ ...point })) : [];

    const drawRouteFromCache = (cache) => {
      const mainPoints = clonePoints(cache?.route?.main);
      if (!mainPoints.length) {
        clearRoute("cache sem dados");
        return;
      }
      routePointsRef.current = { main: mainPoints, alts: {} };
      const zoom = map.getZoom?.() ?? 13;
      const latlngs = toLeafletOptimizedPolyline(
        mainPoints.map((pt) => [pt.lat, pt.lon]),
        { zoom, chunkSize: 1600 }
      );
      clearRoute("desenhar rota cache");
      eventRouteLayerRef.current = L.polyline(latlngs, {
        color: "#ff6600",
        weight: 5,
        opacity: 0.85,
        pane: "eventRoutesPane",
      }).addTo(map);
      const alts = cache?.route?.alts || {};
      Object.entries(alts).forEach(([altId, altValue], idx) => {
        const altPts = clonePoints(altValue?.points);
        if (!altPts.length) return;
        routePointsRef.current.alts[altId] = altPts;
        const zoom = map.getZoom?.() ?? 13;
        const altLatLngs = toLeafletOptimizedPolyline(
          altPts.map((pt) => [pt.lat, pt.lon]),
          { zoom, chunkSize: 1600 }
        );
        const color = ALT_COLORS[idx % ALT_COLORS.length];
        altEventRoutesRef.current[altId] = L.polyline(altLatLngs, {
          color,
          weight: 3,
          dashArray: "4 6",
          opacity: 0.85,
          pane: "eventRoutesPane",
        }).addTo(map);
      });
      if (typeof onRoutePointsChange === "function") {
        onRoutePointsChange();
      }
    };

    // 1) Grupo ativo
    const gid =
      typeof currentGroup === "string"
        ? currentGroup
        : currentGroup?.id || currentGroup?.groupId || null;

    if (!gid) {
      routeLog("[EVENT ROUTE] ⏳ sem gid (transição) → manter rota atual");
      return;
    }

    const group =
      groupsArr.find(
        (g) =>
          g.id === gid ||
          g.groupId === gid ||
          g.firebaseId === gid ||
          g.key === gid
      ) ||
      (currentGroup && typeof currentGroup === "object" ? currentGroup : null);

    if (!group) {
      routeLog("[EVENT ROUTE] ⏳ group ainda não encontrado → manter rota atual");
      return;
    }

    let activeEventId =
      group.activeEventId || group.activeEvent || group.eventId || null;

    const gidCandidates = new Set(
      [group?.id, group?.groupId, group?.firebaseId, group?.key].filter(Boolean)
    );

    // 2) Evento (com fallback seguro)
    const evFromList =
      activeEventId && eventsArr.length
        ? eventsArr.find(
            (e) =>
              e.id === activeEventId ||
              e.id === String(activeEventId) ||
              e.eventId === activeEventId
          ) || null
        : null;

    // 🔥 Fallback: LIVE por groupId quando o grupo ainda não propagou activeEventId
    // ou quando o evento do array ainda não refletiu LIVE ON.
    const evFromLiveGroup =
      gidCandidates.size
        ? eventsArr.find(
            (e) =>
              e?.isLive === true &&
              e?.groupId &&
              (gidCandidates.has(e.groupId) || gidCandidates.has(String(e.groupId)))
          ) || null
        : null;

    // 🔥 Fallback: usar evento direto do grupo (LIVE imediato)
    const ev =
      (evFromList?.isLive === true ? evFromList : null) ||
      evFromLiveGroup ||
      evFromList ||
      (typeof currentGroup === "object" && currentGroup?.event
        ? currentGroup.event
        : null);

    if (!activeEventId && ev) {
      activeEventId = ev?.id || ev?.eventId || ev?.key || null;
    }

    // ✅ Se o grupo ainda não tem activeEventId, mas já existe um LIVE por groupId,
    // seguimos com esse evento em vez de limpar a rota.
    if (!activeEventId && !ev) {
      routeLog("[EVENT ROUTE] ⏳ sem activeEventId nem LIVE por groupId → grace clear");
      scheduleClear("LIVE OFF (group)");
      return;
    }

    // se voltou a ter eventId/evento, cancela limpeza pendente
    cancelPendingClear();

    // ⚠️ Não bloquear só porque events[] ainda não chegou
    if (!eventsReadyRef.current && !activeEventId) {
      routeLog("[EVENT ROUTE] ⏳ eventos ainda não prontos");
      return;
    }

    if (!ev) {
      routeLog("[EVENT ROUTE] ⏳ evento ainda não disponível (aguardar sync)");
      if (eventsReadyRef.current) {
        scheduleClear("evento indisponível");
      }
      return;
    }

    // ✅ LIVE é determinado pelo EVENTO (regra real)
    const isLiveEvent = ev?.isLive === true;

    routeLog("[EVENT ROUTE] 🎯 Evento ativo encontrado", {
      eventId: activeEventId,
      status: ev.status,
      isLiveEvent,
      hasRoute: !!ev.routeGPXUrl,
    });

    // 🚫 LIVE OFF real do EVENTO → limpar (event)
    // (Aqui não usamos grace, porque foi desligado mesmo)
    if (!isLiveEvent) {
      const hasRouteDrawn =
        !!eventRouteLayerRef.current ||
        !!eventDirectionLayerRef.current ||
        Object.keys(altEventRoutesRef.current || {}).length > 0 ||
        Object.keys(extraEventRoutesRef.current || {}).length > 0 ||
        (Array.isArray(routePointsRef.current?.main) &&
          routePointsRef.current.main.length > 0);
      if (hasRouteDrawn) {
        routeLog("[EVENT ROUTE] 🚫 LIVE OFF (event) → limpar rota e sair");
        clearRoute("LIVE OFF (event)");
      } else {
        lastEventRouteKeyRef.current = null;
      }
      return;
    }

    // 🔐 A decisão de acesso já foi feita antes (LIVE + permissões)
    // Aqui apenas validamos se há algo para desenhar

    const normalizeRouteList = (raw, idPrefix) => {
      if (!raw) return [];
      if (Array.isArray(raw)) {
        return raw
          .map((a, idx) => ({
            id: a.id || a.key || `${idPrefix}-${idx}`,
            url: normalizeTrekkinUrl(
              a.url ||
                a.routeGPXUrl ||
                a.routeGpxUrl ||
                a.routeUrl ||
                a.routeFileUrl ||
                a.gpxUrl ||
                a.gpx ||
                (typeof a === "string" ? a : null) ||
                null
            ),
            name: a.name || null,
          }))
          .filter((a) => a.url);
      }
      if (typeof raw === "object") {
        return Object.entries(raw)
          .map(([id, val]) => ({
            id,
            url: normalizeTrekkinUrl(
              val?.url ||
                val?.routeGPXUrl ||
                val?.routeGpxUrl ||
                val?.routeUrl ||
                val?.routeFileUrl ||
                val?.gpxUrl ||
                val?.gpx ||
                (typeof val === "string" ? val : null) ||
                null
            ),
            name: val?.name || null,
          }))
          .filter((a) => a.url);
      }
      return [];
    };

    const routeCandidate =
      typeof getEventRouteForMap === "function"
        ? getEventRouteForMap(activeEventId)
        : null;

    const groupRoute = normalizeTrekkinUrl(
      group?.routeGPXUrl || group?.routeUrl || group?.gpxUrl || null
    );

    routeLog("[EVENT ROUTE] 📦 route sources", {
      activeEventId,
      evRoute: normalizeTrekkinUrl(ev?.routeGPXUrl || null),
      groupRoute,
      storeRoute: normalizeTrekkinUrl(routeCandidate?.routeGPXUrl || null),
    });

    // 3) Determinar rota principal + alternativos
    let url = null;
    let altRoutes = [];
    let tourRoutes = [];

    const rawAlt = ev.alternativeRoutes || ev.altRoutes || ev.alternatives || null;
    altRoutes = normalizeRouteList(rawAlt, "alt");

    const rawTour =
      ev.tourRoutes || ev.tourGPXRoutes || ev.tourGPX || ev.tours || null;
    tourRoutes = normalizeRouteList(rawTour, "tour");

    if (!altRoutes.length && routeCandidate?.altRoutes) {
      altRoutes = normalizeRouteList(routeCandidate.altRoutes, "alt");
    }
    if (!tourRoutes.length && routeCandidate?.tourRoutes) {
      tourRoutes = normalizeRouteList(routeCandidate.tourRoutes, "tour");
    }

    const hasRoute =
      !!ev?.routeGPXUrl ||
      !!groupRoute ||
      !!routeCandidate?.routeGPXUrl ||
      altRoutes.length > 0 ||
      tourRoutes.length > 0;

    if (!hasRoute) {
      clearRoute("sem dados de rota disponíveis");
      return;
    }

    // Em modo LIVE, priorizar sempre o routeGPXUrl do próprio evento
    if (routeCandidate?.routeGPXUrl) {
      url = normalizeTrekkinUrl(routeCandidate.routeGPXUrl);
      routeLog("[EVENT ROUTE] LIVE ativo, usar rota resolvida para o dia", {
        eventId: activeEventId,
        dayIndex: routeCandidate.routeDayIndex ?? null,
        url,
      });
    } else if (groupRoute) {
      url = groupRoute;
      routeLog("[EVENT ROUTE] LIVE ativo, usar rota via group", {
        eventId: activeEventId,
        url,
        altCount: altRoutes.length,
      });
    } else if (ev.routeGPXUrl) {
      url = normalizeTrekkinUrl(ev.routeGPXUrl);
      routeLog("[EVENT ROUTE] LIVE ativo, usar routeGPXUrl do evento", {
        eventId: activeEventId,
        url,
        altCount: altRoutes.length,
      });
    } else {
      // Fallback: tentar regra oficial da store
      let routeObj = null;

      if (typeof getEventRouteForMap === "function") {
        try {
          routeObj = getEventRouteForMap(activeEventId);
        } catch (e) {
          console.warn("[EVENT ROUTE] getEventRouteForMap lançou erro:", e);
        }
      }

      if (routeObj && routeObj.routeGPXUrl) {
        url = normalizeTrekkinUrl(routeObj.routeGPXUrl);

        if (!altRoutes.length && Array.isArray(routeObj.altRoutes)) {
          altRoutes = routeObj.altRoutes
            .map((a, idx) => ({
              id: a.id || a.key || `alt-${idx}`,
              url: normalizeTrekkinUrl(
                a.url ||
                  a.routeGPXUrl ||
                  a.routeUrl ||
                  a.gpxUrl ||
                  a.gpx ||
                  null
              ),
            }))
            .filter((a) => a.url);
        }

        routeLog("[EVENT ROUTE] rota via store (fallback LIVE)", {
          eventId: activeEventId,
          url,
          altCount: altRoutes.length,
        });
      }
    }

    // 4) Chave estável
    const key = JSON.stringify({
      eventId: activeEventId,
      url: url || "",
      alt: altRoutes.map((a) => `${a.id}|${a.url}`).join(","),
      tour: tourRoutes.map((t) => `${t.id}|${t.url}`).join(","),
      live: isLiveEvent,
    });

    if (lastEventRouteKeyRef.current === key) {
      routeLog("[EVENT ROUTE] mesma rota, não redesenhar");
      return;
    }

    if (
      canCacheLiveEvent &&
      eventCacheEntry?.key === key &&
      eventCacheEntry?.route?.main?.length
    ) {
      if (
        eventRouteLayerRef.current ||
        (routePointsRef.current?.main?.length || 0) > 0
      ) {
        lastEventRouteKeyRef.current = key;
        routeLog("[EVENT ROUTE] cache igual já desenhada, não redesenhar");
        return;
      }
      drawRouteFromCache(eventCacheEntry);
      lastEventRouteKeyRef.current = key;
      return;
    }

    lastEventRouteKeyRef.current = key;

    // 5) Carregar trilho principal + alternativos
    (async () => {
      try {
        // limpar primeiro
        clearRoute("desenhar nova rota", { keepKey: true });
        let mainLoaded = false;
        if (url) {
          const fetchUrl = buildGpxFetchUrl(url);
          if (!fetchUrl) {
            console.warn("[EVENT ROUTE] GPX principal sem URL válida");
          } else {
            routeLog("[EVENT ROUTE] fetch GPX principal", fetchUrl);

            const res = await fetch(fetchUrl);
            if (!res.ok) {
              console.warn("[EVENT ROUTE] fetch principal falhou", res.status);
            } else {
              const xml = await res.text();
              const parsed = await parseGPX(xml);

              if (!parsed?.points?.length) {
                console.warn("[EVENT ROUTE] GPX principal sem pontos");
              } else {
                mainLoaded = true;
                if (routePointsRef?.current) {
                  routePointsRef.current.main = parsed.points;
                  routePointsRef.current.alts = routePointsRef.current.alts || {};
                  if (typeof onRoutePointsChange === "function") {
                    onRoutePointsChange();
                  }
                }
                if (canCacheLiveEvent && typeof persistEventRouteCache === "function") {
                  persistEventRouteCache(activeEventId, {
                    key,
                    route: { url, main: parsed.points },
                  });
                }

                const zoom = map.getZoom?.() ?? 13;
                const pts = toLeafletOptimizedPolyline(
                  parsed.points.map((p) => [p.lat, p.lon]),
                  { zoom, chunkSize: 1600 }
                );
                if (lastEventRouteKeyRef.current !== key) {
                  routeLog("[EVENT ROUTE] stale principal ignored", activeEventId);
                  return;
                }

                // trilho principal
                eventRouteLayerRef.current = L.polyline(pts, {
                  color: "#ff6600",
                  weight: 5,
                  opacity: 0.85,
                  pane: "eventRoutesPane",
                }).addTo(map);

                routeLog("[EVENT ROUTE] trilho principal desenhado", pts.length);
              }
            }
          }
        }

        // 6) Alternativos, cada um com a sua cor 🎨
        altRoutes.forEach(async (alt, idx) => {
          if (!alt?.id || !alt?.url) return;

          try {
            const fetchUrl = buildGpxFetchUrl(alt.url);
            if (!fetchUrl) return;
            routeLog("[EVENT ROUTE] fetch alt", alt.id, fetchUrl);

            const resAlt = await fetch(fetchUrl);
            if (!resAlt.ok) {
              console.warn("[EVENT ROUTE] fetch alt falhou", alt.id, resAlt.status);
              return;
            }

            const xmlAlt = await resAlt.text();
            const parsedAlt = await parseGPX(xmlAlt);

            if (!parsedAlt?.points?.length) return;
            if (lastEventRouteKeyRef.current !== key) {
              routeLog("[EVENT ROUTE] stale alt ignored", alt.id);
              return;
            }

            if (routePointsRef?.current) {
              routePointsRef.current.alts = routePointsRef.current.alts || {};
              routePointsRef.current.alts[alt.id] = parsedAlt.points;
              if (typeof onRoutePointsChange === "function") {
                onRoutePointsChange();
              }
            }
            if (canCacheLiveEvent && typeof persistEventRouteCache === "function") {
              persistEventRouteCache(activeEventId, {
                key,
                route: {
                  alts: {
                    [alt.id]: {
                      url: alt.url,
                      points: parsedAlt.points,
                    },
                  },
                },
              });
            }

            const zoom = map.getZoom?.() ?? 13;
            const ptsAlt = toLeafletOptimizedPolyline(
              parsedAlt.points.map((p) => [p.lat, p.lon]),
              { zoom, chunkSize: 1600 }
            );
            const color = ALT_COLORS[idx % ALT_COLORS.length];

            altEventRoutesRef.current[alt.id] = L.polyline(ptsAlt, {
              color,
              weight: 3,
              dashArray: "4 6",
              opacity: 0.85,
              pane: "eventRoutesPane",
            }).addTo(map);

            routeLog("[EVENT ROUTE] trilho alternativo desenhado", alt.id, ptsAlt.length, color);
          } catch (eAlt) {
            console.warn("[EVENT ROUTE] erro alt", alt.id, eAlt);
          }
        });

        // 7) Rotas tour (linha contínua)
        tourRoutes.forEach(async (tour, idx) => {
          if (!tour?.id || !tour?.url) return;

          try {
            const fetchUrl = buildGpxFetchUrl(tour.url);
            if (!fetchUrl) return;
            routeLog("[EVENT ROUTE] fetch tour", tour.id, fetchUrl);

            const resTour = await fetch(fetchUrl);
            if (!resTour.ok) {
              console.warn("[EVENT ROUTE] fetch tour falhou", tour.id, resTour.status);
              return;
            }

            const xmlTour = await resTour.text();
            const parsedTour = await parseGPX(xmlTour);

            if (!parsedTour?.points?.length) return;
            if (lastEventRouteKeyRef.current !== key) {
              routeLog("[EVENT ROUTE] stale tour ignored", tour.id);
              return;
            }

            if (routePointsRef?.current) {
              routePointsRef.current.alts = routePointsRef.current.alts || {};
              routePointsRef.current.alts[`tour-${tour.id}`] = parsedTour.points;
              if (typeof onRoutePointsChange === "function") {
                onRoutePointsChange();
              }
            }
            if (canCacheLiveEvent && typeof persistEventRouteCache === "function") {
              persistEventRouteCache(activeEventId, {
                key,
                route: {
                  tours: {
                    [tour.id]: {
                      url: tour.url,
                      points: parsedTour.points,
                    },
                  },
                },
              });
            }

            const zoom = map.getZoom?.() ?? 13;
            const ptsTour = toLeafletOptimizedPolyline(
              parsedTour.points.map((p) => [p.lat, p.lon]),
              { zoom, chunkSize: 1600 }
            );
            const color = TOUR_COLORS[idx % TOUR_COLORS.length];

            altEventRoutesRef.current[`tour-${tour.id}`] = L.polyline(ptsTour, {
              color,
              weight: 4,
              opacity: 0.9,
              pane: "eventRoutesPane",
            }).addTo(map);

            routeLog("[EVENT ROUTE] rota tour desenhada", tour.id, ptsTour.length, color);
          } catch (eTour) {
            console.warn("[EVENT ROUTE] erro tour", tour.id, eTour);
          }
        });

        if (!mainLoaded && !altRoutes.length && !tourRoutes.length) {
          routeLog("[EVENT ROUTE] sem rota principal e sem alternativos");
        }
      } catch (e) {
        console.warn("[EVENT ROUTE] erro GPX principal", e);
        clearRoute("erro na rotina async principal");
      }
    })();
  }, [
    mapReady,
    isViewerMode,
    currentGroup,
    events,
    groups,
    eventCacheEntry,
    canCacheLiveEvent,
    getEventRouteForMap,
    persistEventRouteCache,
  ]);

  // limpeza de timers ao desmontar
  useEffect(() => {
    return () => {
      try {
        if (pendingClearTimerRef.current) clearTimeout(pendingClearTimerRef.current);
      } catch {}
    };
  }, []);
}
